import React, { useMemo, useState } from 'react';
import Form from '../../Form';
import {
  AFFILIATE_BEHAVIOR_OPTIONS,
  affiliateCodeToFormValues,
  buildAffiliatePreview,
  formatCents,
  serializeAffiliateCodeForm,
  validateAffiliateCodeForm,
} from './affiliateForm';

export default function AffiliateCodeForm({
  code,
  planOptions,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const [values, setValues] = useState(() => affiliateCodeToFormValues(code));
  const [errors, setErrors] = useState({});
  const preview = useMemo(
    () => buildAffiliatePreview(values, planOptions),
    [values, planOptions],
  );
  const fields = [
    {
      type: 'text',
      name: 'code',
      label: 'Affiliate code',
      required: true,
      placeholder: 'YOURNAME10',
      maxLength: 64,
      help: 'Letters, numbers, and hyphens. Spaces are removed when saved.',
    },
    {
      type: 'text',
      name: 'name',
      label: 'Internal name',
      placeholder: 'Summer referral offer',
      maxLength: 128,
    },
    {
      type: 'textarea',
      name: 'description',
      label: 'Public description',
      placeholder: 'A short description shown when someone checks this code.',
      groupStyle: { flexBasis: '100%' },
    },
    {
      type: 'select',
      name: 'behavior',
      label: 'Affiliate code type',
      options: AFFILIATE_BEHAVIOR_OPTIONS.map(({ value, label }) => ({ value, label })),
      required: true,
      help: AFFILIATE_BEHAVIOR_OPTIONS.find(option => option.value === values.behavior)
        ?.description || 'Choose one of the three supported types before saving this code.',
    },
    {
      type: 'number',
      name: 'maxRedemptions',
      label: 'Maximum referrals',
      min: 1,
      step: 1,
      help: 'Leave blank for unlimited.',
    },
    {
      type: 'date',
      name: 'expiresAt',
      label: 'Expiration date',
      help: 'Leave blank for no expiration.',
    },
  ];

  const submit = formValues => {
    const nextErrors = validateAffiliateCodeForm(formValues);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSubmit(serializeAffiliateCodeForm(formValues));
  };

  return (
    <div className="affiliate-code-editor">
      <Form
        className="affiliate-code-form flexcol twocol"
        fields={fields}
        values={values}
        errors={errors}
        onChange={nextValues => {
          setValues(nextValues);
          setErrors({});
        }}
        onSubmit={submit}
      >
        <section className="affiliate-offer-preview" aria-label="Offer preview">
          <h3>What this offer means</h3>
          <ul>
            {preview.map(plan => (
              <li key={plan.type}>
                <strong>{plan.label} ({formatCents(plan.priceCents)}):</strong>{' '}
                They save {formatCents(plan.discountCents)}. You earn about{' '}
                {formatCents(plan.commissionCents)}
                {plan.paymentCount > 1 ? ` across ${plan.paymentCount} payments` : ''}.
              </li>
            ))}
          </ul>
          <p className="help">
            Estimates use current list prices. Actual earnings use the payment and frozen code
            terms recorded by Mayhem.
          </p>
        </section>

        <div className="affiliate-form-actions group">
          <button className="button primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : code ? 'Save affiliate code' : 'Create affiliate code'}
          </button>
          {onCancel && (
            <button className="button secondary" type="button" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </Form>
    </div>
  );
}
